import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { UserService } from 'src/app/services/user.service';
import { SettingsService } from 'src/app/services/settings.service';
import { SdaReservation } from 'src/app/shared/models/users/sda-reservation';
import { AddSdaReservationRequest } from 'src/app/shared/models/users/add-sda-reservation-request';
import { UserProfile } from 'src/app/shared/models/users/user-profile';
import { FiaService } from 'src/app/services/fia.service';
import { FiaLeftToApplyFor } from 'src/app/shared/models/users/fia-left-to-apply-for';

@Component({
  selector: 'app-sda-reserved',
  templateUrl: './sda-reserved.component.html',
  styleUrls: ['./sda-reserved.component.scss'],
})
export class SdaReservedComponent implements OnInit, OnChanges {
  @Input() selectedUserProfile: UserProfile;

  loading: boolean = true;
  loadingAvailable: boolean = false;
  errorResponse: HttpErrorResponse;
  showError: boolean = false;

  sdaAvailable: number = 0;
  sdaAvailableError: string = '';
  currentSdaReservedAmount: number = 0;
  reservations: SdaReservation[] = [];
  reservationsByYear: { [year: number]: SdaReservation[] } = {};

  constructor(
    private userService: UserService,
    public settingsService: SettingsService,
    public fiaService: FiaService
  ) {}

  ngOnInit(): void {
    if (this.selectedUserProfile.userId) {
      this.loadReservations();
      this.loadSdaAndFiaAvailable();
      this.currentSdaReservedAmount = this.selectedUserProfile.adminSdaAmount ?? 0;
    } else {
      this.loading = false;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedUserProfile'] && !changes['selectedUserProfile'].firstChange && this.selectedUserProfile.userId) {
      this.loadReservations();
      this.loadSdaAndFiaAvailable();
    }
  }

  loadSdaAndFiaAvailable(): void {
    this.loadingAvailable = true;
    this.fiaService.CalculateLeftToApplyFor(this.selectedUserProfile.userId).subscribe(
      (data: FiaLeftToApplyFor) => {
        this.sdaAvailable = data?.sdaAvailable ?? 0;
        this.sdaAvailableError = '';
        this.loadingAvailable = false;
      },
      (error) => {
        console.error(error);
        this.sdaAvailableError = 'Something went wrong while loading SDA available.';
        this.loadingAvailable = false;
      }
    );
  }

  loadReservations(): void {
    this.loading = true;
    this.userService.GetSdaReservations(this.selectedUserProfile.userId).subscribe(
      (data: SdaReservation[]) => {
        this.reservations = data;
        this.organizeReservationsByYear();
        this.currentSdaReservedAmount = this.calculateTotalReservedForYear(this.currentYear);
        this.loading = false;
      },
      (error) => {
        this.parseAndDisplayError(error);
        this.loading = false;
      }
    );
  }

  organizeReservationsByYear(): void {
    this.reservationsByYear = {};
    this.availableYears.forEach((year) => {
      this.reservationsByYear[year] = this.reservations.filter((r) => r.year === year);
    });
  }

  calculateTotalReservedForYear(year: number): number {
    return this.reservationsByYear[year].reduce((total, reservation) => total + reservation.amount, 0);
  }

  hasDifference(): boolean {
    return this.calculateTotalReservedForYear(this.currentYear) !== this.currentSdaReservedAmount;
  }

  parseAndDisplayError(error: any) {
    this.errorResponse = error;
    this.showError = true;
  }

  currentYear = new Date().getFullYear();
  availableYears = [this.currentYear, this.currentYear + 1];

  newReservationYear: number | null = null;
  newReservationAmount: number | null = null;
  newReservationComment: string = '';

  get isFormValid(): boolean {
    return this.newReservationYear !== null && this.newReservationAmount !== null && this.newReservationComment.trim() !== '';
  }

  addReservation() {
    if (!this.isFormValid) {
      return;
    }

    const request: AddSdaReservationRequest = {
      userId: this.selectedUserProfile.userId,
      amount: this.newReservationAmount,
      year: this.newReservationYear,
      comment: this.newReservationComment,
      actionByUserId: this.settingsService.userProfile.userId,
    };

    this.loading = true;
    this.showError = false;

    this.userService.AddSdaReservation(request).subscribe(
      () => {
        // Clear form after successful add
        this.newReservationYear = null;
        this.newReservationAmount = null;
        this.newReservationComment = '';

        // Reload reservations to show the new one
        this.loadReservations();
        this.loadSdaAndFiaAvailable();
      },
      (error) => {
        this.parseAndDisplayError(error);
        this.loading = false;
      }
    );
  }

  getReservationsForYear(year: number): SdaReservation[] {
    return this.reservationsByYear[year] || [];
  }
}
